import {MultiSelectionBox} from 'notebook/tables/constants/TableSelectionConstants';
import type {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import type {Cell} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';

/**
 * Returns true if any cell within the given box has a merged region in the evaluated table.
 */
export function computeContainsMergedRegion(
  computedTable: Array<Array<EvaluatedCell>>,
  box: MultiSelectionBox
): boolean {
  const {minI, maxI, minJ, maxJ} = box;
  for (let i = minI; i <= maxI; i++) {
    for (let j = minJ; j <= maxJ; j++) {
      if (computedTable[i][j] != null && computedTable[i][j].mergedBox != null) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Returns true if any cell other than the top-left cell in the given box has non-empty text.
 */
export function computeHasTextInMergedRegion(table: Array<Array<Cell>>, box: MultiSelectionBox): boolean {
  const {minI, maxI, minJ, maxJ} = box;
  for (let i = minI; i <= maxI; i++) {
    for (let j = minJ; j <= maxJ; j++) {
      if (i === minI && j === minJ) {
        continue;
      }
      const cell = table[i][j];
      if (cell.text !== '') {
        return true;
      }
    }
  }
  return false;
}

/**
 * Finds all distinct merged boxes whose top-left corner falls within the given selection box.
 */
export function computeMergedBoxesInSelection(
  computedTable: Array<Array<EvaluatedCell>>,
  box: MultiSelectionBox
): Array<MultiSelectionBox> {
  const {minI, maxI, minJ, maxJ} = box;
  const mergedBoxes: Array<MultiSelectionBox> = [];
  for (let i = minI; i <= maxI; i++) {
    for (let j = minJ; j <= maxJ; j++) {
      const evaluatedCell = computedTable[i][j];
      if (evaluatedCell != null && evaluatedCell.mergedBox != null) {
        if (evaluatedCell.mergedBox.minI === i && evaluatedCell.mergedBox.minJ === j) {
          mergedBoxes.push(evaluatedCell.mergedBox);
        }
      }
    }
  }
  return mergedBoxes;
}

/**
 * Computes the bounding rectangle of all merged regions that transitively overlap with the
 * given cell position (i, j) within the provided selection box.
 *
 * The algorithm sorts merged boxes by row and column, then iteratively expands the overlap
 * boundaries until no further expansion is possible.
 */
export function computeAreMergedBoxesOverlapping(
  computedTable: Array<Array<EvaluatedCell>>,
  box: MultiSelectionBox,
  cellI: number,
  cellJ: number
): {overMinI: number; overMaxI: number; overMinJ: number; overMaxJ: number} {
  const mergedBoxes = computeMergedBoxesInSelection(computedTable, box);
  let [overMinI, overMaxI, overMinJ, overMaxJ] = [cellI, cellI, cellJ, cellJ];

  const rowSortedBoxes = mergedBoxes.slice().sort((a, b) => TableUtil.rowCmpMergedBoxes(a, b));
  const colSortedBoxes = mergedBoxes.slice().sort((a, b) => TableUtil.colCmpMergedBoxes(a, b));

  for (let idx = 0; idx < mergedBoxes.length; idx++) {
    const forwardRowBox = rowSortedBoxes[idx];
    const backwardRowBox = rowSortedBoxes[mergedBoxes.length - idx - 1];
    const forwardColBox = colSortedBoxes[idx];
    const backwardColBox = colSortedBoxes[mergedBoxes.length - idx - 1];

    if (backwardRowBox.minI <= overMinI && overMinI <= backwardRowBox.maxI) {
      overMinI = Math.min(overMinI, backwardRowBox.minI);
    }

    if (forwardRowBox.minI <= overMaxI && overMaxI <= forwardRowBox.maxI) {
      overMaxI = Math.max(overMaxI, forwardRowBox.maxI);
    }

    if (backwardColBox.minJ <= overMinJ && overMinJ <= backwardColBox.maxJ) {
      overMinJ = Math.min(overMinJ, backwardColBox.minJ);
    }

    if (forwardColBox.minJ <= overMaxJ && overMaxJ <= forwardColBox.maxJ) {
      overMaxJ = Math.max(overMaxJ, forwardColBox.maxJ);
    }
  }
  return {overMinI, overMaxI, overMinJ, overMaxJ};
}
